import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fullName } from '../utils/format.js';

/**
 * A comment box where typing "@" offers the people you can call out.
 *
 * A row of buttons for every colleague stops working somewhere around a dozen
 * accounts, so the mention is written where it is read - inside the sentence -
 * and the list only appears while you are typing a name after "@".
 *
 * What gets sent is derived from the text at submit time (`mentionsIn`): delete
 * the "@Sarah" you typed and Sarah is no longer notified, which is what
 * deleting it plainly means.
 */

/** "@IT" is a group: it reaches every IT account at once. */
export const mentionOptions = (people, t) => [
  { token: 'ROLE:IT', label: t('comments.mentionIT'), hint: t('comments.mentionITHint') },
  ...people.map((person) => ({
    token: `USER:${person.id}`,
    label: fullName(person),
    hint: t(`roles.${person.role}`),
  })),
];

/** The tokens whose "@label" still appears in the text. */
export const mentionsIn = (text, options) =>
  options.filter((option) => text.includes(`@${option.label}`)).map((option) => option.token);

const MentionInput = ({ value, onChange, options, rows = 3, placeholder, inputRef }) => {
  const { t } = useTranslation();
  // The formatting toolbar acts on this same textarea, so the caller can pass
  // the ref in rather than a second box appearing next to this one.
  const localRef = useRef(null);
  const ref = inputRef ?? localRef;
  const [query, setQuery] = useState(null); // text typed after "@", or null

  const matches = useMemo(() => {
    if (query === null) return [];
    const needle = query.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(needle)).slice(0, 6);
  }, [query, options]);

  /** Look back from the caret for an unfinished "@word". */
  const readQuery = (text, caret) => {
    const before = text.slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at === -1) return null;
    const typed = before.slice(at + 1);
    // A space ends a mention unless it is inside a name we could still match.
    if (typed.includes('\n') || typed.length > 24) return null;
    return typed;
  };

  const handle = (event) => {
    const text = event.target.value;
    onChange(text);
    setQuery(readQuery(text, event.target.selectionStart));
  };

  const choose = (option) => {
    const field = ref.current;
    const caret = field?.selectionStart ?? value.length;
    const before = value.slice(0, caret);
    const at = before.lastIndexOf('@');
    const next = `${value.slice(0, at)}@${option.label} ${value.slice(caret)}`;
    onChange(next);
    setQuery(null);
    // Put the caret straight after the name so typing can continue.
    requestAnimationFrame(() => {
      const position = at + option.label.length + 2;
      field?.focus();
      field?.setSelectionRange(position, position);
    });
  };

  return (
    <div className="mention">
      <textarea
        ref={ref}
        className="input"
        rows={rows}
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={handle}
        onBlur={() => setTimeout(() => setQuery(null), 120)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setQuery(null);
          if (event.key === 'Enter' && matches.length && query !== null) {
            event.preventDefault();
            choose(matches[0]);
          }
        }}
      />

      {matches.length ? (
        <ul className="mention__list">
          {matches.map((option) => (
            <li key={option.token}>
              <button type="button" className="mention__option" onMouseDown={() => choose(option)}>
                <span>@{option.label}</span>
                <span className="muted">{option.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {query === null ? <p className="mention__hint muted">{t('comments.mentionHint')}</p> : null}
    </div>
  );
};

export default MentionInput;
